import React, { forwardRef, useEffect, useState } from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import { Label, Col, Row } from "reactstrap";
import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import { AntSelect } from "@/CommonElements/AntSelect";
import { AntInput } from "@/CommonElements/AntInput";
import {
  useActiveNationalitiesQuery,
  useMakkahPilgrimGroupNameMappings,
  useMissionsByCountryQuery,
  useMissionCompaniesQuery,
} from "@services/basicData/basicData";
import { Stack } from "react-bootstrap";
import { cn } from "@utils/helpers";
import { useLocalizedText } from "@utils/hooks/useLocalizedText";
import { SelectCountry } from "@Component/Form/SelectCountry/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";

export const InitialGroupForm = forwardRef(
  ({ initialData = {}, canAmend, isViewMode = false }, ref) => {
    const formContext = useFormContext();
    const internalForm = useForm({
      defaultValues: initialData,
    });
    const form = formContext || internalForm;
    const isExternalForm = !!formContext;
    const resolvedCanAmend =
      typeof canAmend === "boolean" ? canAmend : !isViewMode;

    if (typeof ref === "function") {
      ref(form);
    } else if (ref) {
      ref.current = form;
    }

    const {
      control,
      formState: { errors },
      watch,
      setValue,
      setError,
      reset,
      getValues,
    } = form;

    // Expose setError as setFormError on the form instance for backward compatibility
    if (form) {
      form.setFormError = setError;
    }

    const { t } = useTranslation();
    const { getLocalizedText } = useLocalizedText();
    const [groupIdentifierOptions, setGroupIdentifierOptions] = useState([]);
    const [isLoadingGroupIdentifiers, setIsLoadingGroupIdentifiers] =
      useState(false);

    // Track initialization state
    const [isInitializing, setIsInitializing] = useState(true);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Watch form values for dependent fields
    const orgnizerCountry = useWatch({ control, name: "organizer_country" });
    const delegation = useWatch({ control, name: "delegation" });

    const { isLoading: isLoadingOrganizerCountries, isFetching: isFetchingOrganizerCountries } =
      useActiveNationalitiesQuery();

    const {
      data: missions = [],
      isLoading: isLoadingMissions,
      isFetching: isFetchingMissions,
    } = useMissionsByCountryQuery(orgnizerCountry);

    const {
      data: missionCompanies = { data: { data: { companies: [] } } },
      isLoading: isLoadingMissionCompanies,
      isFetching: isFetchingMissionCompanies,
    } = useMissionCompaniesQuery(delegation);

    const delegationOptions = React.useMemo(
      () =>
        missions.map(({ value, labelAr = "", labelEn = "", q = "" }) => ({
          value,
          label: [value, getLocalizedText(labelAr, labelEn) || value].join(
            " - "
          ),
          q: q || [labelAr, labelEn].filter(Boolean).join(","),
        })),
      [missions, getLocalizedText]
    );

    const delegationLabel = React.useMemo(() => <SelectCachedOptions.Display
      value={delegation}
      optionKey="missions"
      campareBy="id"
      render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en) || option?.id].join(" - ")}
    />
      , [delegation, getLocalizedText]);

    const affiliatedOrganizerOptions = React.useMemo(
      () =>
        missionCompanies?.data?.data?.companies?.map(
          ({ id, companyNameEn = "", companyNameAr = "", q = "" }) => ({
            value: String(id),
            label: [id, getLocalizedText(companyNameAr, companyNameEn)].join(
              " - "
            ),
            q: q || [companyNameAr, companyNameEn].filter(Boolean).join(","),
          })
        ),
      [missionCompanies, getLocalizedText]
    );

    const affiliatedOrganizerValue = watch("affiliated_organizer");
    const affiliatedOrganizerLabel = React.useMemo(() => <SelectCachedOptions.Display
      value={affiliatedOrganizerValue}
      optionKey="companies"
      campareBy="id"
      render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en)].filter(Boolean).join(" - ")}
    />, [affiliatedOrganizerValue, getLocalizedText]);

    const {
      data: pilgrimGroupMappingsRes,
      isLoading: isLoadingPilgrimGroupMappings,
      isFetching: isFetchingPilgrimGroupMappings,
    } = useMakkahPilgrimGroupNameMappings({
      mission_id: delegation,
      company_id: affiliatedOrganizerValue,
    });

    const mappedGroupNameOptions = React.useMemo(() => {
      const list = pilgrimGroupMappingsRes?.data?.data?.data ?? [];
      return (Array.isArray(list) ? list : []).map((item) => ({
        value: item.id,
        label:
          item.mapped_group_name ??
          "",
      }));
    }, [pilgrimGroupMappingsRes]);

    const selectedMappedGroupName =
      initialData?.pilgrim_group_name_mapping?.mapped_group_name ||
      initialData?.mapped_group_name ||
      watch("group_name");

    // Initialize form with data - load all dependent options first, then reset form
    useEffect(() => {
      if (
        isExternalForm ||
        hasInitialized ||
        !initialData ||
        Object.keys(initialData).length === 0
      ) {
        return;
      }

      const initializeForm = async () => {
        setIsInitializing(true);
        const data = { ...initialData };

        // Step 3: Now reset form with all data after options are loaded
        reset(data);
        setHasInitialized(true);
        setIsInitializing(false);
      };

      initializeForm();
    }, [initialData, reset, hasInitialized, isExternalForm]);

    // Fetch delegations and nationalities when country changes (user interaction)
    useEffect(() => {
      // Skip if initializing or if country hasn't changed from initial data
      if (isExternalForm || isInitializing || !orgnizerCountry) {
        return;
      }

      // Only reset if user manually changed the country (not during initialization)
      if (
        hasInitialized &&
        orgnizerCountry !== initialData?.organizer_country
      ) {
        setGroupIdentifierOptions([]);
        setValue("delegation", "");
        const currentNationality = getValues("nationality");
        if (
          currentNationality &&
          String(currentNationality) !== String(orgnizerCountry)
        ) {
          setValue("nationality", "");
        }
        setValue("affiliated_organizer", "");
        setValue("group_identifier", "");
      }
    }, [
      orgnizerCountry,
      setValue,
      getValues,
      isInitializing,
      hasInitialized,
      initialData?.organizer_country,
      isExternalForm,
    ]);

    // Fetch affiliated organizers and group identifiers when delegation changes (user interaction)
    useEffect(() => {
      // Skip if initializing or if delegation hasn't changed from initial data
      if (isExternalForm || isInitializing || !delegation) {
        return;
      }

      // Only reset if user manually changed the delegation (not during initialization)
      if (hasInitialized && delegation !== initialData?.delegation) {
        setGroupIdentifierOptions([]);
        setValue("affiliated_organizer", "");
        setValue("group_identifier", "");
        setValue("group_name", "");
        setValue("pilgrim_group_name_mapping_id", "");
        setIsLoadingGroupIdentifiers(true);
      }
    }, [
      delegation,
      setValue,
      isInitializing,
      hasInitialized,
      initialData?.delegation,
      isExternalForm,
    ]);

    // Determine if group_identifier should be enabled/required
    const groupIdentifierEnabled = groupIdentifierOptions.length > 0;

    const groupNumberValue =
      initialData?.group_number ??
      initialData?.group_identifier ??
      initialData?.arrival_group_number ??
      initialData?.arrival_group_identifier ??
      "-";

    return (
      <>
        {/* {draftContextHolder} */}
        <Row>
          {/* Organizer Country */}
          <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label
                for="organizer_country"
                className={cn("fw-bold", resolvedCanAmend && "required")}
              >
                {t("ORGANIZER_COUNTRY")}
              </Label>
              {!resolvedCanAmend && (
                <SelectCountry.Display value={watch("organizer_country")} />
              )}
              {resolvedCanAmend && (
                <Controller
                  control={control}
                  name="organizer_country"
                  rules={{
                    required: t("ORGANIZER_COUNTRY_REQUIRED"),
                  }}
                  render={({ field }) => (
                    <>
                      <SelectCountry
                        {...field}
                        id="organizer_country"
                        placeholder={t("ORGANIZER_COUNTRY_PLACEHOLDER")}
                        status={errors.organizer_country ? "error" : undefined}
                        loading={
                          isLoadingOrganizerCountries ||
                          isFetchingOrganizerCountries
                        }
                        disabled={
                          isLoadingOrganizerCountries ||
                          isFetchingOrganizerCountries
                        }
                        onChange={(val) => {
                          field.onChange(val);
                          setValue("nationality", val, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                      {errors.organizer_country && (
                        <div className="text-danger mt-1">
                          {errors.organizer_country.message}
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </Stack>
          </Col>

          {/* Nationality */}
          <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label
                for="nationality"
                className={cn("fw-bold", resolvedCanAmend && "required")}
              >
                {t("NATIONALITY")}
              </Label>
              {!resolvedCanAmend && (
                <SelectCountry.Display value={watch("nationality")} />
              )}
              {resolvedCanAmend && (
                <Controller
                  control={control}
                  name="nationality"
                  rules={{
                    required: t("NATIONALITY_REQUIRED"),
                  }}
                  render={({ field }) => (
                    <>
                      <SelectCountry
                        {...field}
                        id="nationality"
                        placeholder={t("NATIONALITY_PLACEHOLDER")}
                        status={errors.nationality ? "error" : undefined}
                        loading={
                          isLoadingOrganizerCountries ||
                          isFetchingOrganizerCountries
                        }
                        disabled={
                          !orgnizerCountry ||
                          isLoadingOrganizerCountries ||
                          isFetchingOrganizerCountries
                        }
                        onChange={(val) => field.onChange(val)}
                      />

                      {errors.nationality && (
                        <div className="text-danger mt-1">
                          {errors.nationality.message}
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </Stack>
          </Col>

          {/* Delegation */}
          <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label for="delegation" className={"fw-bold"}>
                {t("DELEGATION")}
              </Label>
              {!resolvedCanAmend &&
                (isLoadingMissions || isFetchingMissions ? (
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: "100%" }}
                  />
                ) : (
                  <div>{delegationLabel}</div>
                ))}
              {resolvedCanAmend && (
                <Controller
                  control={control}
                  name="delegation"
                  render={({ field }) => (
                    <>
                      <AntSelect
                        id="delegation"
                        showSearch
                        optionFilterProp="q"
                        placeholder={t("DELEGATION_PLACEHOLDER")}
                        status={errors.delegation ? "error" : undefined}
                        options={delegationOptions}
                        loading={isLoadingMissions || isFetchingMissions}
                        disabled={
                          !orgnizerCountry ||
                          isLoadingMissions ||
                          isFetchingMissions
                        }
                        onChange={(val) => field.onChange(val)}
                        value={field.value}
                      />
                      {errors.delegation && (
                        <div className="text-danger mt-1">
                          {errors.delegation.message}
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </Stack>
          </Col>

          {/* Affiliated Organizer */}
          <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label for="affiliated_organizer" className={"fw-bold"}>
                {t("AFFILIATED_ORGANIZER")}
              </Label>
              {!resolvedCanAmend &&
                (isLoadingMissionCompanies || isFetchingMissionCompanies ? (
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: "100%" }}
                  />
                ) : (
                  <div>{affiliatedOrganizerLabel}</div>
                ))}
              {resolvedCanAmend && (
                <Controller
                  control={control}
                  name="affiliated_organizer"
                  render={({ field }) => (
                    <>
                      <AntSelect
                        id="affiliated_organizer"
                        showSearch
                        optionFilterProp="q"
                        placeholder={t("AFFILIATED_ORGANIZER_PLACEHOLDER")}
                        status={
                          errors.affiliated_organizer ? "error" : undefined
                        }
                        options={affiliatedOrganizerOptions}
                        loading={
                          isLoadingMissionCompanies ||
                          isFetchingMissionCompanies
                        }
                        disabled={
                          !delegation ||
                          isLoadingMissionCompanies ||
                          isFetchingMissionCompanies
                        }
                        onChange={(val) => field.onChange(val)}
                        value={field.value}
                      />
                      {errors.affiliated_organizer && (
                        <div className="text-danger mt-1">
                          {errors.affiliated_organizer.message}
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </Stack>
          </Col>

          {/* Group Identifier */}
          {groupIdentifierEnabled && (
            <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
              <Stack gap={1} className="mb-3">
                <Label for="group_identifier" className={"fw-bold"}>
                  {t("GROUP_IDENTIFIER")}
                </Label>
                {!resolvedCanAmend &&
                  (isLoadingGroupIdentifiers ? (
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div>{watch("group_identifier") ?? "-"}</div>
                  ))}
                {resolvedCanAmend && (
                  <Controller
                    control={control}
                    name="group_identifier"
                    render={({ field }) => (
                      <>
                        <AntSelect
                          id="group_identifier"
                          showSearch
                          placeholder={t("GROUP_IDENTIFIER_PLACEHOLDER")}
                          status={errors.group_identifier ? "error" : undefined}
                          options={groupIdentifierOptions}
                          loading={isLoadingGroupIdentifiers}
                          disabled={
                            !delegation ||
                            isLoadingGroupIdentifiers ||
                            !groupIdentifierEnabled
                          }
                          onChange={(val) => {
                            field.onChange(val);
                            if (val) {
                              setValue("group_name", "");
                            }
                          }}
                          value={field.value}
                        />
                        {errors.group_identifier && (
                          <div className="text-danger mt-1">
                            {errors.group_identifier.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                )}
              </Stack>
            </Col>
          )}

          {/* Group Number (read-only display) */}
          {!resolvedCanAmend && (
            <Col xs={6} md={4}>
              <Stack gap={1} className="mb-3">
                <Label className="fw-bold">{t("GROUP_NUMBER")}</Label>
                <div>{groupNumberValue}</div>
              </Stack>
            </Col>
          )}

          {/* Organizer Name */}
          <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label
                className={cn("fw-bold", resolvedCanAmend && "required")}
                htmlFor="pilgrim_group_name_mapping_id"
              >
                {t("MAPPED_GROUP_NAME")}
              </Label>
              {!resolvedCanAmend && (
                <div>{selectedMappedGroupName || "-"}</div>
              )}
              {resolvedCanAmend && (
                <Controller
                  name="pilgrim_group_name_mapping_id"
                  control={control}
                  rules={{
                    required: t("FIELD_REQUIRED"),
                  }}
                  render={({ field }) => (
                    <AntSelect
                      {...field}
                      id="pilgrim_group_name_mapping_id"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      placeholder={t("MAPPED_GROUP_NAME")}
                      options={mappedGroupNameOptions}
                      loading={
                        isLoadingPilgrimGroupMappings ||
                        isFetchingPilgrimGroupMappings
                      }
                      disabled={
                        !delegation ||
                        isLoadingPilgrimGroupMappings ||
                        isFetchingPilgrimGroupMappings
                      }
                      status={errors.pilgrim_group_name_mapping_id ? "error" : undefined}
                    />
                  )}
                />
              )}
              {errors.pilgrim_group_name_mapping_id && resolvedCanAmend && (
                <div className="text-danger mt-1">
                  {errors.pilgrim_group_name_mapping_id.message}
                </div>
              )}
            </Stack>
          </Col>

          {/* Organizer Number (10 digits) */}
          <Col xs={resolvedCanAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label
                className={cn("fw-bold")}
                htmlFor="supervisor_mobile_number"
              >
                {t("MOBILE_NUMBER")}
              </Label>
              {!resolvedCanAmend && (
                <div>{watch("supervisor_mobile_number") || "-"}</div>
              )}
              {resolvedCanAmend && (
                <Controller
                  name="supervisor_mobile_number"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^05\d{8}$/,
                      message: "يجب أن يبدأ بـ 05 ويتكون من 10 أرقام",
                    },
                  }}
                  render={({ field }) => (
                    <AntInput
                      id="supervisor_mobile_number"
                      name="supervisor_mobile_number"
                      type="text"
                      placeholder={t("ORGANIZER_NUMBER_PLACEHOLDER")}
                      inputMode="numeric"
                      maxLength={10}
                      {...field}
                      status={
                        errors.supervisor_mobile_number ? "error" : undefined
                      }
                    />
                  )}
                />
              )}
              {errors.supervisor_mobile_number && resolvedCanAmend && (
                <div className="text-danger mt-1">
                  {errors.supervisor_mobile_number.message}
                </div>
              )}
            </Stack>
          </Col>

          {/* Service Center (string per spec) */}
          {/* <Col xs={canAmend ? 12 : 6} md={4}>
            <Stack gap={1} className="mb-3">
              <Label className="fw-bold" htmlFor="service_center_id">
                {t("SERVICE_CENTER")}
              </Label>
              {!canAmend && (
                <div>
                  {(() => {
                    const serviceCenterCode = watch("service_center_id");
                    if (!serviceCenterCode) return "-";
                    const serviceCenter = (
                      basicData?.service_centers || []
                    ).find(
                      (sc) => String(sc.code) === String(serviceCenterCode)
                    );
                    return serviceCenter
                      ? serviceCenter.name
                      : serviceCenterCode || "-";
                  })()}
                </div>
              )}
              {canAmend && (
                <Controller
                  name="service_center_id"
                  control={control}
                  render={({ field }) => (
                    <SelectCachedOptions
                      {...field}
                      id="service_center_id"
                      disabled={isLoadingBasicData || isFetchingBasicData}
                      placeholder={t("SERVICE_CENTER_PLACEHOLDER")}
                      status={errors.service_center_id ? "error" : undefined}
                      optionKey="service_centers"
                      options={(serviceCenter) => ({
                        value: serviceCenter.id,
                        label: serviceCenter.name,
                      })}
                    />
                  )}
                />
              )}
            </Stack>
          </Col> */}
        </Row>
      </>
    );
  }
);
